import { useTranslation } from "react-i18next";
import { Linkedin } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import alexandrePhoto from "@/assets/team/alexandre-profit.webp";
import gregoirePhoto from "@/assets/team/gregoire-profit.webp";
import brahimPhoto from "@/assets/team/brahim-zebbar.webp";
import sarahPhoto from "@/assets/team/sarah-favre.webp";
import nicolasPhoto from "@/assets/team/nicolas-bel.webp";
import stanislasPhoto from "@/assets/team/stanislas-de-montlebert.webp";
import logoSolarImpulse from "@/assets/logos/solar-impulse-white.webp";

const AboutSection = () => {
  const { t } = useTranslation();

  const openExternalProfile = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const founders = [
    {
      photo: alexandrePhoto,
      name: "Alexandre Profit",
      role: t("about.alexRole"),
      tag: t("about.founder"),
      desc: t("about.alexDesc"),
      badges: ["Aquaphys", "HEC Challenge+"],
      linkedin: "https://www.linkedin.com/in/alexandre-profit-8895725/",
    },
    {
      photo: gregoirePhoto,
      name: "Grégoire Profit",
      role: t("about.gregRole"),
      tag: t("about.founder"),
      desc: t("about.gregDesc"),
      badges: ["École Polytechnique", "HEC Challenge+"],
      linkedin: "https://www.linkedin.com/in/gr%C3%A9goire-profit-51bb19/",
    },
    {
      photo: brahimPhoto,
      name: "Brahim Zebbar",
      role: t("about.brahimRole"),
      tag: t("about.associate"),
      desc: t("about.brahimDesc"),
      badges: ["Akamai", "Indeed"],
      linkedin: "https://www.linkedin.com/in/zebbarbrahim/",
    },
  ];

  const team = [
    { photo: sarahPhoto, name: "Sarah Favre", role: t("about.sarahRole"), desc: "Heliotec · InfraRed Capital Partners · London Business School", linkedin: "https://www.linkedin.com/in/sarahfavre/" },
    { photo: nicolasPhoto, name: "Nicolas Bel", role: t("about.nicolasRole"), desc: "CentraleSupélec · CFM · PIWWOP", linkedin: "https://www.linkedin.com/in/nico-bel-742a351/" },
    { photo: stanislasPhoto, name: "Stanislas de Montlebert", role: t("about.stanislasRole"), desc: "HEC Paris · CentraleSupélec · Veolia", linkedin: "https://www.linkedin.com/in/sdemontlebert/" },
  ];

  const credBadges = [
    { icon: "🚀", text: t("about.cred1") },
    { icon: "🏆", text: t("about.cred2") },
    { icon: "🔬", text: t("about.cred3") },
    { icon: "🌐", text: t("about.cred4") },
  ];

  return (
    <section id="about" className="py-8 lg:py-14 relative" aria-labelledby="about-heading">
      <div className="container mx-auto px-4 space-y-8">
        <ScrollReveal>
          <div className="space-y-4 max-w-3xl">
            <p className="section-label">{t("about.label")}</p>
            <h2 id="about-heading" className="font-display text-3xl lg:text-5xl text-foreground">{t("about.title")}</h2>
            <p className="text-muted-foreground font-body text-lg">{t("about.desc")}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {founders.map((f) => (
              <div key={f.name} className="card-surface p-6 flex flex-col items-center text-center gap-4 group transition-all duration-500 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10">
                <div className="relative">
                  <img src={f.photo} alt={f.name} className="w-32 h-32 rounded-full object-cover border-2 border-primary/30 grayscale group-hover:grayscale-0 transition-all duration-500" />
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.15em] uppercase font-body font-medium bg-primary/20 text-primary px-2 py-0.5 rounded-full whitespace-nowrap">{f.tag}</span>
                </div>
                <div className="space-y-1 pt-2">
                  <div className="flex items-center justify-center gap-2">
                    <p className="font-display text-lg text-foreground">{f.name}</p>
                    <button
                      type="button"
                      onClick={() => openExternalProfile(f.linkedin)}
                      aria-label={`LinkedIn ${f.name}`}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Linkedin size={16} />
                    </button>
                  </div>
                  <p className="text-xs text-primary font-body font-medium">{f.role}</p>
                  <p className="text-xs text-muted-foreground font-body mt-1">{f.desc}</p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 mt-auto pt-2">
                  {f.badges.map((b) => (
                    <span key={b} className="text-[10px] font-body text-muted-foreground bg-secondary/60 px-2 py-0.5 rounded">{b}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <p className="text-sm text-primary font-body font-medium uppercase tracking-wider mb-4">{t("about.advisoryBoard")}</p>
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((m) => (
              <div key={m.name} className="card-surface p-6 flex items-center gap-4 group transition-all duration-500 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10">
                <img src={m.photo} alt={m.name} className="w-24 h-24 rounded-full object-cover border-2 border-primary/30 grayscale group-hover:grayscale-0 transition-all duration-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-display text-lg text-foreground">{m.name}</p>
                    <button
                      type="button"
                      onClick={() => openExternalProfile(m.linkedin)}
                      aria-label={`LinkedIn ${m.name}`}
                      className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                    >
                      <Linkedin size={16} />
                    </button>
                  </div>
                  <p className="text-xs text-primary font-body font-medium">{m.role}</p>
                  <p className="text-xs text-muted-foreground font-body mt-1">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {credBadges.map((b) => (
              <div key={b.text} className="card-surface p-4 flex items-center gap-4">
                <span className="text-2xl">{b.icon}</span>
                <span className="text-sm font-body text-foreground">{b.text}</span>
              </div>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.3}>
          <div className="flex justify-center pt-2">
            <a
              href="https://solarimpulse.com/companies/aquaphys#"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Member of World Alliance for Efficient Solutions by Solar Impulse Foundation"
              className="opacity-85 hover:opacity-100 transition-opacity"
            >
              <img
                src={logoSolarImpulse}
                alt="Member of World Alliance for Efficient Solutions by Solar Impulse Foundation"
                className="h-14 md:h-16 w-auto"
                loading="lazy"
              />
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default AboutSection;
